import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import { CreateTagInput } from '~/services/TagsService';
import { formatDateLongISO } from '~/services/TimeService';

import type { AssignmentFormData } from './TagAssignments/TagAssignments';
import TagForm, { type FormTag } from './TagForm';
import { useCreateTag } from './hooks';

export default function TagCreate({ onBack }: { onBack: () => void }) {
  const [assignments, setAssignments] = useState<Array<AssignmentFormData>>([]);
  const { enqueueSnackbar } = useSnackbar();

  const createTag = useCreateTag();

  const { control, handleSubmit } = useForm<FormTag>({
    defaultValues: {
      name: '',
      schedules: [],
    },
  });

  const onSubmit = async (data: FormTag) => {
    if (assignments.length === 0) {
      enqueueSnackbar('Please add at least one assignment before creating the tag', { variant: 'error' });
      return;
    }

    const schedules =
      data.schedules.length > 0
        ? data.schedules
        : [
            {
              brands: ['luxuryescapes'],
              regions: getRegionNamesAndCode().map((r) => r.code),
              activeStartDate: formatDateLongISO(new Date()),
              activeEndDate: formatDateLongISO(new Date(new Date().setFullYear(new Date().getFullYear() + 100))),
            },
          ];

    const createTagData: CreateTagInput = {
      name: data.name,
      assignments: assignments.map((assignment) => ({
        entityType: assignment.entityType,
        entityId: assignment.entityId,
      })),
      schedules: schedules.map((schedule) => ({
        brands: schedule.brands,
        regions: schedule.regions,
        activeStartDate: schedule.activeStartDate,
        activeEndDate: schedule.activeEndDate,
        travelStartDate: schedule.travelStartDate || undefined,
        travelEndDate: schedule.travelEndDate || undefined,
      })),
    };
    await createTag.mutateAsync(createTagData);

    onBack();
  };

  const handleSave = handleSubmit(onSubmit, (errors) => {
    enqueueSnackbar(errors['schedules']?.message || 'Unknown error', { variant: 'error' });
  });

  return (
    <TagForm
      mode="create"
      control={control}
      isSaving={createTag.isPending}
      assignments={assignments}
      onAssignmentsChange={setAssignments}
      onBack={onBack}
      onSave={handleSave}
    />
  );
}
